<!-- Last Time I… — the primary screen.

     HAND-WRITTEN, not generated. The plan asks for "every task sorted by how
     overdue it is, each with a plain-language 'last done' line, and mark one
     done in a single tap", and the generator emits a list of names — which is a
     scaffold, not the product. `write_app_file` makes this the app's own, so a
     later `apply_plan` skips it instead of overwriting.

     Design decisions and where they come from:

       · STATUS IS NEVER COLOUR ALONE. Every row says "Overdue by 2 months" in
         words as well as red. Colour is invisible to a screen reader, gone in
         greyscale, and unreliable for roughly one man in twelve.
       · DONE IS ONE TAP AND UNDOABLE. A confirm dialog on the primary action
         would defeat the point of the screen; an undo in the toast prevents the
         same mistake without spending a tap on every correct one.
       · TOUCH TARGETS are 48px, above the 44pt iOS / 48dp Android floor, with
         12px between the row and the button so a thumb aiming at one cannot
         catch the other.
       · EVERY COLOUR IS AN IONIC TOKEN. No hex in this file: the theme owns the
         palette, and dark mode then works without a second set of rules. -->
<template>
  <IonPage>
    <IonHeader :translucent="true">
      <IonToolbar>
        <IonTitle>Last Time I…</IonTitle>
        <IonButtons slot="end">
          <IonButton aria-label="Add task" @click="goAdd">
            <IonIcon slot="icon-only" :icon="addOutline" />
          </IonButton>
          <IonButton aria-label="Settings" @click="goSettings">
            <IonIcon slot="icon-only" :icon="settingsOutline" />
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent :fullscreen="true">
      <IonHeader collapse="condense">
        <IonToolbar><IonTitle size="large">Last Time I…</IonTitle></IonToolbar>
      </IonHeader>

      <div v-if="!rows.length" class="empty">
        <h2>Nothing yet</h2>
        <p>Nothing tracked yet. Add the first thing you can never remember doing.</p>
        <IonButton @click="goAdd">Add task</IonButton>
      </div>

      <IonList v-else :inset="true">
        <!-- `:key` is the row id, never the index: a keyed list reuses the
             wrong DOM node when the order changes, and this list REORDERS the
             moment anything is marked done. -->
        <IonItem v-for="row in rows" :key="row.task.id" lines="full">
          <IonLabel class="row" @click="open(row.task)">
            <h2>{{ row.task.name }}</h2>
            <!-- TWO LINES, not one wrapped one. These were a single flex row
                 with a separator dot between them, and on a phone the second
                 half wrapped and left the dot stranded at the end of the first
                 — which reads as a rendering fault rather than a separator.
                 Stacked, each says one thing and neither can orphan. -->
            <p class="status"><IonText :color="row.tone">{{ row.status }}</IonText></p>
            <p class="since">last done {{ row.lastLabel }}</p>
          </IonLabel>

          <!-- `fill="clear"`, deliberately. Outlined at full size this was a
               138px box in caps, louder than the task name it belongs to — the
               least important text on the row winning the most attention. The
               tap target is unchanged at 48px; only the ink is quieter. -->
          <IonButton
            slot="end"
            fill="clear"
            size="small"
            class="done"
            :disabled="busy === row.task.id"
            @click.stop="markDone(row.task)"
          >
            {{ doneCta }}
          </IonButton>
        </IonItem>
      </IonList>
    </IonContent>

    <!-- The undo lives here rather than in a confirm dialog. Marking something
         done is reversible and common; deleting the entry is the rare case, so
         the rare case pays the cost. -->
    <IonToast
      :is-open="!!toast"
      :message="toast?.message"
      :duration="5000"
      position="bottom"
      :buttons="toastButtons"
      @didDismiss="toast = null"
    />
  </IonPage>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonItem, IonLabel,
  IonButtons, IonButton, IonIcon, IonText, IonToast,
} from '@ionic/vue';
import { settingsOutline, addOutline } from 'ionicons/icons';
import { useRouter } from 'vue-router';
import { tasks, completions, addCompletion, removeCompletion } from '../store.js';
import { sortedTasks } from '../lib/schedule.js';

const router = useRouter();
const busy = ref(null);
const toast = ref(null);

// The plan's own word for this action.
const doneCta = 'Done today';

const rows = computed(() => sortedTasks(tasks.value, completions.value));

const open = (task) => router.push({ name: 'task-detail', params: { id: task.id } });
const goAdd = () => router.push({ name: 'task-form' });
const goSettings = () => router.push({ name: 'settings' });

/**
 * One tap, and say so.
 *
 * Completing an action silently is the commonest way an app feels broken: the
 * row moves, and without a word for it that reads as a glitch rather than as
 * the thing you just asked for.
 */
async function markDone (task) {
  busy.value = task.id;
  try {
    const now = new Date();
    const entry = addCompletion({
      task: task.id,
      doneAt: now.toISOString().slice(0, 10),
      loggedAt: now.toISOString(),
    });

    // A short tap on a real device, and nothing at all where the API is absent
    // — the web build and the simulator both land here.
    try {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch { /* no haptics on this platform */ }

    toast.value = { message: `${task.name} — logged today`, entryId: entry.id };
  } finally {
    busy.value = null;
  }
}

const toastButtons = computed(() => [
  {
    text: 'Undo',
    role: 'cancel',
    handler: () => {
      if (toast.value?.entryId != null) removeCompletion(toast.value.entryId);
    },
  },
]);
</script>

<style scoped>
.empty { padding: 3rem 1.25rem; text-align: center; color: var(--ion-color-medium-shade); }
.empty h2 { color: var(--ion-text-color); font-size: 1.25rem; margin: 0 0 .5rem; }
.empty p { margin: 0 0 1.5rem; }

/* The whole label is the tap target for opening the task, so it needs the
   height of one — 48px clears both platform minimums. */
.row { cursor: pointer; padding: .5rem 0; min-height: 48px; }
.row h2 { font-size: 1rem; font-weight: 600; margin: 0 0 .25rem; }

/* The status carries the colour, so it leads. `.since` is context and sits
   below it in a stepped grey — `medium-shade` rather than `medium`, because the
   lighter step does not clear 4.5:1 on the card. */
.status { font-size: .875rem; margin: 0 0 .125rem; font-weight: 500; }
.since { font-size: .8125rem; color: var(--ion-color-medium-shade); margin: 0; }

/* 12px from the row's own tap area, comfortably past the 8px minimum, so a
   thumb aiming at Done cannot open the task instead. The visual weight is low
   but the target is not: 48px either way. */
.done {
  min-height: 48px;
  margin-inline-start: 12px;
  font-size: .875rem;
  font-weight: 600;
  transition: opacity 120ms ease;
}
.done:active { opacity: .6; }

@media (prefers-reduced-motion: reduce) {
  .done { transition: none; }
}
</style>
